// Orchestrator for browser broadcasting. Owns the capture stream, encoders,
// muxer, transport and adaptive-bitrate controller, and exposes a small
// imperative API to the UI.
//
// States: idle -> preview -> connecting -> publishing <-> paused
//         publishing -> reconnecting -> publishing (transport auto-heals)
//         any -> ended | error
//
// Note: the UI should treat "publishing + segments being acked" (uplinkKbps
// flowing) or the AppSync event subscription as confirmation of being live.

import { getCapabilities } from "../capabilities";
import { acquireStream, stopStream, listDevices } from "./mediaDevices";
import {
  createVideoPipeline,
  pickVideoCodec,
  VIDEO_TARGET_BITRATE,
  VIDEO_FLOOR_BITRATE,
  VIDEO_FPS,
} from "./videoPipeline";
import { createAudioPipeline, createLevelMonitor } from "./audioPipeline";
import { createAdaptiveController } from "./adaptiveController";
import { createStreamingMuxer } from "./muxer";
import { createTransport } from "./transport";
import { createLocalRecorder } from "./localRecorder";
import { createCompositor } from "./compositor";
import { createRestartQueue } from "./restartQueue";

export { listDevices };
export { isLocalRecordingSupported, chooseRecordingFile } from "./localRecorder";

export function createBroadcastEngine({
  relayUrl,
  streamKey,
  onState,
  onStats,
  onError,
  onRecordingChange,
  onScreenShareChange,
  onStreamRenewed,
}) {
  const caps = getCapabilities();
  let state = "idle";
  let stream = null;
  let videoEl = null;
  let codec = null;
  let transport = null;
  let muxer = null;
  let videoPipeline = null;
  let audioPipeline = null;
  let levelMonitor = null;
  let controller = null;
  let muted = false;
  let wantLive = false; // user intent: keep publishing across reconnects
  let pausing = false;
  let ending = false;
  let statsTimer = null;
  let wakeLock = null;
  let encodedWindow = []; // { at, bytes } muxed output for encoder kbps
  let recordingHandle = null; // armed pre-live via setRecordingFile()
  let recorder = null;
  let screenTrack = null; // active getDisplayMedia track
  let compositor = null; // screen+camera PiP mix while screensharing
  let lastDeviceIds = {}; // last chosen devices, for silent re-acquire

  const currentVideoTrack = () =>
    compositor?.track || screenTrack || stream?.getVideoTracks()[0];

  const ended = (track) => !track || track.readyState === "ended";

  // Remember the device Chrome ACTUALLY opened (not just what was requested),
  // so post-sleep renewals re-open the same physical camera/mic instead of
  // whatever the fallback picks (e.g. a virtual cam).
  const rememberDevices = (s) => {
    const v = s.getVideoTracks()[0]?.getSettings().deviceId;
    const a = s.getAudioTracks()[0]?.getSettings().deviceId;
    lastDeviceIds = { videoDeviceId: v || lastDeviceIds.videoDeviceId, audioDeviceId: a || lastDeviceIds.audioDeviceId };
  };

  // Laptop sleep / device unplug ends tracks silently; construct-time APIs
  // (MediaStreamTrackProcessor) then throw "Input track cannot be ended".
  // Re-acquire dead capture before it's used, and tell the UI so the preview
  // element gets the fresh stream.
  async function ensureLiveTracks() {
    if (screenTrack && ended(screenTrack)) {
      screenTrack = null;
      compositor?.stop();
      compositor = null;
      onScreenShareChange?.(false);
    }
    if (!stream || ended(stream.getVideoTracks()[0]) || ended(stream.getAudioTracks()[0])) {
      const oldCam = stream?.getVideoTracks()[0];
      const next = await acquireStream(lastDeviceIds);
      if (stream) stopStream(stream);
      stream = next;
      rememberDevices(stream);
      watchTrackEnds(stream);
      levelMonitor?.attach(stream);
      if (compositor && oldCam) compositor.replace(oldCam, stream.getVideoTracks()[0]);
      onStreamRenewed?.(compositor ? new MediaStream([compositor.track]) : stream);
    }
  }

  let renewing = false;
  function watchTrackEnds(watched) {
    for (const track of watched.getTracks()) {
      track.onended = async () => {
        if (wantLive || renewing || watched !== stream) return; // live path renews via startPipelines
        renewing = true;
        try {
          await ensureLiveTracks();
        } catch {
          // user will see the preview error + Try Again when they interact
        } finally {
          renewing = false;
        }
      };
    }
  }

  // Swap the encoded video source while live: seamless if dimensions match,
  // otherwise a quick publish restart with a fresh init segment.
  async function swapToTrack(newTrack) {
    const publishing = videoPipeline && (state === "publishing" || state === "reconnecting");
    if (!publishing || !newTrack) return;
    const s = newTrack.getSettings();
    if (s.width === videoPipeline.width && s.height === videoPipeline.height) {
      videoPipeline.replaceTrack(newTrack);
    } else {
      await restartPipelines();
    }
  }

  // Serialized restarts (see restartQueue.js).
  const restarts = createRestartQueue({
    stop: () => stopPipelines(),
    start: () => startPipelines(),
    canStart: () => wantLive && !pausing,
  });
  const restartPipelines = restarts.run;
  const restartSettled = restarts.settled;
  const restartInBackground = () => {
    if (wantLive && !pausing) restartPipelines().catch((e) => onError?.("engine", e?.message));
  };

  const setState = (next) => {
    if (state === next || state === "ended") return;
    state = next;
    onState?.(next);
  };

  const requestWakeLock = async () => {
    try {
      wakeLock = await navigator.wakeLock?.request("screen");
    } catch {
      // not supported / denied — banner in UI covers this
    }
  };
  const onVisibility = () => {
    if (document.visibilityState !== "visible") return;
    if (wantLive) requestWakeLock();
    // Coming back from sleep/background with dead tracks: refresh the preview.
    else if (stream && state !== "idle") ensureLiveTracks().catch(() => {});
  };
  const onBeforeUnload = (e) => {
    if (wantLive) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  async function startPipelines() {
    stopStatsLoop();
    await ensureLiveTracks();
    muxer = createStreamingMuxer({
      fps: VIDEO_FPS,
      onInit: (bytes) => transport.sendInit(bytes),
      onSegment: (bytes) => {
        encodedWindow.push({ at: Date.now(), bytes: bytes.byteLength });
        transport.sendSegment(bytes);
      },
      onError: (e) => onError?.("muxer", e?.message),
    });
    audioPipeline = await createAudioPipeline({
      stream,
      onChunk: (chunk, meta) => muxer?.addAudioChunk(chunk, meta),
      onRawAudio: (audioData) => recorder?.encodeAudioData(audioData),
      onError: (e) => onError?.("audio", e?.message),
    });
    audioPipeline.setMuted(muted);
    videoPipeline = createVideoPipeline({
      track: currentVideoTrack(),
      videoEl,
      framePath: caps.videoFramePath,
      codec,
      onChunk: (chunk, meta) => muxer?.addVideoChunk(chunk, meta),
      onRawFrame: (frame) => recorder?.encodeVideoFrame(frame),
      // Device rotated: restart the publish with the new dimensions.
      onOrientationChange: restartInBackground,
      onError: (e) => onError?.("video", e?.message),
    });
    controller = createAdaptiveController({
      targetBitrate: VIDEO_TARGET_BITRATE,
      floorBitrate: VIDEO_FLOOR_BITRATE,
      onChange: ({ bitrate, fps }) => videoPipeline?.reconfigure({ bitrate, fps }),
    });
    startStatsLoop();
    // A pause or fatal error during this start already set the state.
    if (wantLive && !pausing) setState("publishing");
  }

  async function stopPipelines() {
    stopStatsLoop();
    const v = videoPipeline;
    const a = audioPipeline;
    const m = muxer;
    videoPipeline = audioPipeline = muxer = controller = null;
    await v?.stop();
    await a?.stop();
    // Flush the muxer tail so ffmpeg receives the last fragments before stop.
    await m?.finalize();
  }

  function startStatsLoop() {
    statsTimer = setInterval(() => {
      const t = transport?.getStats() || {};
      const adaptive = controller?.tick(t);
      const now = Date.now();
      while (encodedWindow.length && now - encodedWindow[0].at > 3000) encodedWindow.shift();
      const encoderKbps = Math.round((encodedWindow.reduce((s, x) => s + x.bytes, 0) * 8) / 3000);
      onStats?.({
        ...t,
        encoderKbps,
        targetKbps: adaptive ? Math.round(adaptive.bitrate / 1000) : Math.round(VIDEO_TARGET_BITRATE / 1000),
        fps: adaptive?.fps ?? VIDEO_FPS,
        quality: adaptive?.quality ?? "good",
      });
    }, 1000);
  }
  function stopStatsLoop() {
    clearInterval(statsTimer);
    statsTimer = null;
  }

  return {
    get state() {
      return state;
    },
    supported: caps.tier === "webcodecs",

    async startPreview({ videoDeviceId, audioDeviceId, videoElement } = {}) {
      videoEl = videoElement || videoEl;
      lastDeviceIds = { videoDeviceId, audioDeviceId };
      const next = await acquireStream({ videoDeviceId, audioDeviceId });
      if (stream) stopStream(stream);
      stream = next;
      rememberDevices(stream);
      watchTrackEnds(stream);
      if (levelMonitor) levelMonitor.attach(stream);
      else levelMonitor = createLevelMonitor(stream);
      const { width, height } = stream.getVideoTracks()[0]?.getSettings() || {};
      codec = await pickVideoCodec(width || 1280, height || 720);
      if (!codec) throw new Error("H.264 encoding not supported on this device");
      setState("preview");
      return stream;
    },

    // Device switch that also works while live. Same-size camera swaps are
    // seamless (track replacement + forced keyframe); a resolution change
    // restarts the publish (brief hiccup) with a fresh init segment.
    async setDevices({ videoDeviceId, audioDeviceId } = {}) {
      lastDeviceIds = { videoDeviceId, audioDeviceId };
      const next = await acquireStream({ videoDeviceId, audioDeviceId });
      const old = stream;
      const publishing = videoPipeline && (state === "publishing" || state === "reconnecting");
      if (!publishing) {
        stream = next;
        if (old) stopStream(old);
        levelMonitor?.attach(stream);
        return stream;
      }
      const oldCam = old?.getVideoTracks()[0];
      stream = next;
      rememberDevices(stream);
      watchTrackEnds(stream);
      levelMonitor?.attach(stream);
      audioPipeline?.replaceTrack(next.getAudioTracks()[0]);
      if (compositor) {
        // Screensharing: the compositor consumes the camera; the encoded
        // track (compositor output) is untouched.
        compositor.replace(oldCam, next.getVideoTracks()[0]);
      } else if (!screenTrack) {
        await swapToTrack(next.getVideoTracks()[0]);
      }
      if (old) stopStream(old);
      return compositor ? new MediaStream([compositor.track]) : stream;
    },

    // Screen sharing (desktop): replaces the encoded video source; mic keeps
    // coming from the selected microphone. Reverts automatically when the
    // user stops sharing from the browser UI.
    async startScreenShare() {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { width: { max: 1280 }, height: { max: 720 }, frameRate: { ideal: 30 } },
        audio: false,
      });
      const track = display.getVideoTracks()[0];
      screenTrack = track;
      track.addEventListener("ended", () => {
        this.stopScreenShare().catch(() => {});
      });
      // Screen full-frame with the camera as a PiP bubble; layout is swappable
      // live via swapScreenShareLayout(). The compositor's output track has
      // constant 1280x720 dims so layout changes never touch the pipeline.
      const camTrack = stream?.getVideoTracks()[0];
      compositor = createCompositor({
        mainTrack: track,
        pipTrack: camTrack && camTrack.readyState === "live" ? camTrack : null,
      });
      await swapToTrack(compositor.track);
      onScreenShareChange?.(true);
      return new MediaStream([compositor.track]); // for the preview element
    },

    // Toggle screen-first <-> camera-first while sharing. Instant.
    swapScreenShareLayout() {
      compositor?.swap();
    },

    async stopScreenShare() {
      if (!screenTrack) return stream;
      screenTrack.stop();
      screenTrack = null;
      compositor?.stop();
      compositor = null;
      await swapToTrack(stream?.getVideoTracks()[0]);
      onScreenShareChange?.(false);
      return stream;
    },

    // Arm/disarm local recording (desktop). Takes a FileSystemFileHandle from
    // chooseRecordingFile(); the recorder itself starts at goLive.
    setRecordingFile(handle) {
      recordingHandle = handle;
      onRecordingChange?.(handle ? "armed" : null);
    },

    async goLive() {
      if (!stream) throw new Error("no preview");
      wantLive = true;
      pausing = false;
      ending = false;
      setState("connecting");
      if (recordingHandle && !recorder) {
        const { width, height } = stream.getVideoTracks()[0]?.getSettings() || {};
        try {
          recorder = await createLocalRecorder({
            fileHandle: recordingHandle,
            videoCodec: codec,
            width: width || 1280,
            height: height || 720,
            fps: VIDEO_FPS,
            sampleRate: 48000,
            onError: (e) => onError?.("recording", e?.message),
          });
          onRecordingChange?.("recording");
        } catch (e) {
          recorder = null;
          onError?.("recording", e?.message);
        }
      }
      requestWakeLock();
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("beforeunload", onBeforeUnload);
      // Go Live again after an error: the old connection must not linger
      // (its reconnects would restart these pipelines).
      transport?.close();
      transport = createTransport({
        url: relayUrl,
        streamKey,
        handlers: {
          // Fresh session (first connect, post-expiry reconnect, or after
          // restart-needed): start/restart pipelines with a new init segment.
          onReady: restartInBackground,
          onResumed: () => {
            if (wantLive && !pausing) setState("publishing");
          },
          onRestartNeeded: restartInBackground,
          onIdleTimeout: () => {
            // The relay saw no video for a while. If we mean to be live,
            // retry right away; otherwise stay disconnected until resumeLive.
            if (wantLive && !pausing) transport.connect();
          },
          onConnectionState: (cs) => {
            if (cs === "reconnecting" && wantLive && !pausing) setState("reconnecting");
          },
          onStopped: () => {
            if (ending) return;
            if (pausing) setState("paused");
          },
          onFatal: (code, message) => {
            wantLive = false;
            restartSettled().then(stopPipelines);
            setState("error");
            onError?.(code, message);
          },
        },
      });
      transport.connect();
    },

    async pause() {
      pausing = true;
      await restartSettled();
      await stopPipelines();
      transport?.stop();
      setState("paused"); // relay confirms via "stopped"; UI can be immediate
      // Finalize the local recording so the file is complete and safe.
      if (recorder) {
        const r = recorder;
        recorder = null;
        recordingHandle = null;
        await r.stop();
        onRecordingChange?.("saved");
      }
    },

    async resumeLive() {
      if (!transport) return this.goLive();
      pausing = false;
      if (transport.suspended) {
        // The relay closed the idle connection while we were paused; onReady
        // starts the pipelines once it's back.
        setState("connecting");
        transport.connect();
        return;
      }
      await restartPipelines();
    },

    setMuted(next) {
      muted = !!next;
      audioPipeline?.setMuted(muted);
    },

    getAudioLevels() {
      return levelMonitor?.getLevels() ?? [0, 0];
    },

    async end() {
      ending = true;
      wantLive = false;
      await restartSettled();
      await stopPipelines();
      transport?.stop();
      if (recorder) {
        const r = recorder;
        recorder = null;
        await r.stop();
        onRecordingChange?.("saved");
      }
      // Give the stop message a beat to flush before closing the socket.
      setTimeout(() => transport?.close(), 300);
      setState("ended");
      this.destroy(false);
    },

    destroy(hard = true) {
      wantLive = false;
      stopStatsLoop();
      if (recorder) {
        const r = recorder;
        recorder = null;
        r.stop(); // fMP4 stays playable even if this doesn't finish
      }
      if (screenTrack) {
        screenTrack.stop();
        screenTrack = null;
      }
      compositor?.stop();
      compositor = null;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
      wakeLock?.release().catch(() => {});
      wakeLock = null;
      levelMonitor?.close();
      levelMonitor = null;
      if (hard) {
        transport?.close();
        stopPipelines();
        restartSettled().then(stopPipelines);
      }
      if (stream) {
        stopStream(stream);
        stream = null;
      }
    },
  };
}
